import { Box, LoadingOverlay, Paper, Text, Title } from "@mantine/core";
import {
  getColorVisionDetails,
  getColorVisionRecommendation,
  getColorVisionScore,
  getColorVisionStatus,
  getContrastDetails,
  getContrastScore,
  getContrastStatus,
  getVisionSummary,
  getVisualFieldDetails,
  getVisualFieldStatus,
} from "./utils/testCalculations";

import { ColorVisionTestCard } from "./components/ColorVisionTestCard";
import { ContrastTestCard } from "./components/ContrastTestCard";
import { IconEye } from "@tabler/icons-react";
import { PatientHeader } from "./components/PatientHeader";
import { Printable } from "@components";
import { VFAFlatMetrics } from "types/tests/specifics";
import { VisualFieldTestCard } from "./components/VisualFieldTestCard";
import { buildOverallStatus } from "./utils/overallStatus";
import { useVisionTestData } from "./hooks/useVisionTestData";

export interface VisionTestResultsProps {
  vfUuid1?: string;
  vfUuid2?: string;
  contrastUuid?: string;
  colorVisionUuid?: string;
}

function getEyeLabel(test: any): string {
  if (test?.eye === "leftEye") return "Left Eye";
  if (test?.eye === "rightEye") return "Right Eye";
  return "Both Eyes";
}

function buildVfResult(vfTest: any, vfMapValues: VFAFlatMetrics | null) {
  const isC40 = vfTest?.testType === "C-40";
  const pointValues = isC40 ? (Object.values(vfTest?.points ?? {}) as number[]) : [];
  const pointsSeen = isC40 ? pointValues.filter((value) => value === 1).length : undefined;
  const pointsMissed = isC40 ? pointValues.filter((value) => value === 0).length : undefined;
  const totalPoints =
    pointsSeen != null && pointsMissed != null ? pointsSeen + pointsMissed : undefined;
  const efficiency =
    totalPoints != null && totalPoints > 0 ? (pointsSeen! / totalPoints) * 100 : undefined;
  const screeningDetails = !isC40
    ? undefined
    : efficiency == null || totalPoints == null || pointsSeen == null || pointsMissed == null
      ? "C-40 screening data is available, but point-based summary metrics could not be calculated."
      : `${pointsSeen} of ${totalPoints} screening points were seen${pointsMissed > 0 ? `, with ${pointsMissed} missed points requiring review.` : "."}`;
  const screeningStatus =
    !isC40 || efficiency == null ? undefined : efficiency < 95 ? "attention" : "normal";

  return {
    title: "Visual Field Test",
    description: "Measures the full horizontal and vertical range of what you can see",
    status: isC40 ? screeningStatus : getVisualFieldStatus(vfMapValues),
    vfi: vfMapValues?.vfi?.toFixed(0) || "N/A",
    hasMapData: !!vfTest && (!!vfMapValues || isC40),
    details: isC40 ? screeningDetails : getVisualFieldDetails(vfMapValues),
    recommendation: isC40
      ? "Review missed screening points together with the C-40 map and clinical history."
      : "Continue regular check-ups annually",
    isC40,
    pointsSeen,
    pointsMissed,
    efficiency,
  };
}

export default function VisionTestResults({
  vfUuid1,
  vfUuid2,
  contrastUuid,
  colorVisionUuid,
}: VisionTestResultsProps = {}) {
  const {
    patientData,
    vfTest1,
    vfMapValues1,
    vfTest2,
    vfMapValues2,
    contrastTest,
    colorVisionTest,
    colorVisionImages,
    hasVf1,
    hasVf2,
    hasContrast,
    hasColorVision,
    loading,
  } = useVisionTestData({ vfUuid1, vfUuid2, contrastUuid, colorVisionUuid });

  const vfResult1 = hasVf1 && vfTest1 ? buildVfResult(vfTest1, vfMapValues1) : null;
  const vfResult2 = hasVf2 && vfTest2 ? buildVfResult(vfTest2, vfMapValues2) : null;
  const contrastResultStatus = hasContrast && contrastTest ? getContrastStatus(contrastTest) : null;
  const colorVisionResultStatus =
    hasColorVision && colorVisionTest ? getColorVisionStatus(colorVisionTest) : null;

  const subStatuses = [
    vfResult1?.status,
    vfResult2?.status,
    contrastResultStatus,
    colorVisionResultStatus,
  ];

  const overallStatus =
    buildOverallStatus(subStatuses, {
      normal: "Overall: Normal",
      monitor: "Overall: Monitor",
      attention: "Overall: Attention Required",
    }) ?? undefined;

  const summarySubResults = [
    vfResult1 ? { title: "Visual Field", status: vfResult1.status } : null,
    vfResult2 ? { title: "Visual Field", status: vfResult2.status } : null,
    contrastResultStatus ? { title: "Contrast Sensitivity", status: contrastResultStatus } : null,
    colorVisionResultStatus ? { title: "Color Vision", status: colorVisionResultStatus } : null,
  ].filter(
    (entry): entry is { title: string; status: string } => entry !== null && entry.status != null,
  );

  return (
    <Printable.Section>
      <Box>
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <PatientHeader
          patientData={patientData}
          icon={<IconEye color="white" size={24} />}
          title="Vision Screening Results"
          subtitle="Eye Examination Report"
          overallStatus={overallStatus}
        />

        {hasVf1 && vfTest1 && vfResult1 && (
          <Box mt="md">
            <VisualFieldTestCard
              test={vfResult1}
              visualFieldTest={vfTest1}
              vfMapValues={vfMapValues1}
              eyeLabel={getEyeLabel(vfTest1)}
            />
          </Box>
        )}

        {hasVf2 && vfTest2 && vfResult2 && (
          <Box mt="md">
            <VisualFieldTestCard
              test={vfResult2}
              visualFieldTest={vfTest2}
              vfMapValues={vfMapValues2}
              eyeLabel={getEyeLabel(vfTest2)}
            />
          </Box>
        )}

        {hasContrast && contrastTest && (
          <Box mt="md" style={{ breakInside: "avoid" }}>
            <Paper shadow="md" p="lg" radius="md" withBorder>
              <ContrastTestCard
                test={{
                  title: "Contrast Sensitivity Test",
                  description: "Measures ability to distinguish between light and dark",
                  status: getContrastStatus(contrastTest),
                  score: getContrastScore(contrastTest),
                  hasContrastData: !!contrastTest.results,
                  contrastResults: contrastTest.results?.contrastResults,
                  pelliRobsonResults: contrastTest.results?.pelliRobsonResults,
                  details: getContrastDetails(contrastTest),
                  recommendation: "Results are age-appropriate and healthy",
                }}
              />
            </Paper>
          </Box>
        )}

        {hasColorVision && colorVisionTest && (
          <Box mt="md">
            <Paper shadow="md" p="lg" radius="md" withBorder>
              <ColorVisionTestCard
                test={{
                  title: "Color Vision Test",
                  description: "Checks ability to see and distinguish different colors",
                  status: getColorVisionStatus(colorVisionTest),
                  score: getColorVisionScore(colorVisionTest),
                  hasColorVisionData: !!colorVisionTest.results,
                  colorVisionResults: colorVisionTest.results,
                  details: getColorVisionDetails(colorVisionTest),
                  recommendation: getColorVisionRecommendation(colorVisionTest),
                }}
                colorVisionImages={colorVisionImages}
              />
            </Paper>
          </Box>
        )}

        <Paper
          shadow="md"
          p="xl"
          radius="md"
          mt="md"
          withBorder
          style={{
            backgroundColor: "var(--oculera-tint-orange)",
            borderColor: "var(--oculera-tint-orange-border)",
          }}
        >
          <Title order={4} mb="md" c="orange.8">
            Summary
          </Title>
          <Text size="sm" c="orange.9">
            {getVisionSummary(patientData.name, summarySubResults)}
          </Text>
        </Paper>

        <Text size="xs" c="dimmed" ta="center" mt="md" mb="md">
          This report is for informational purposes. Please consult with your ophthalmologist for
          medical advice.
        </Text>
      </Box>
    </Printable.Section>
  );
}
